import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router'

import { type RouterProps } from 'apps/types'
import { QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'

import { QuoteRequestLayout } from './QuoteRequestLayout'
import { CensusUploadPage } from './steps/CensusUploadPage'
import { ClientDetailsPage } from './steps/ClientDetailsPage'
import { OtherFilesUploadPage } from './steps/OtherFilesUploadPage'
import { SbcsUploadPage } from './steps/SbcsUploadPage'
import { SummaryEditPage } from './steps/SummaryEditPage'
import { SummaryPage } from './steps/SummaryPage'

export enum QuoteRequestStep {
  CLIENT_DETAILS = 'client-details',
  CENSUS_UPLOAD = 'upload/census',
  SBCS_UPLOAD = 'upload/sbcs',
  OTHER_FILES_UPLOAD = 'upload/other',
  SUMMARY = 'summary',
  SUMMARY_EDIT = 'summary/edit',
}

interface QuoteRequestFlowProps {
  dashboardUrl: string
  initialStep: QuotingProspectQuoteRequestStatus
  allowedCensusTypes: string[]
  allowedSbcTypes: string[]
  allowedOtherFileTypes: string[]
}

type QuoteRequestFlowPropsUnion = QuoteRequestFlowProps & RouterProps

function getStepRoute (status: QuotingProspectQuoteRequestStatus): QuoteRequestStep {
  switch (status) {
    case QuotingProspectQuoteRequestStatus.client_details:
      return QuoteRequestStep.CLIENT_DETAILS
    case QuotingProspectQuoteRequestStatus.census:
      return QuoteRequestStep.CENSUS_UPLOAD
    case QuotingProspectQuoteRequestStatus.sbcs:
      return QuoteRequestStep.SBCS_UPLOAD
    case QuotingProspectQuoteRequestStatus.other_files:
      return QuoteRequestStep.OTHER_FILES_UPLOAD
    case QuotingProspectQuoteRequestStatus.summary:
      return QuoteRequestStep.SUMMARY
    default:
      return QuoteRequestStep.CLIENT_DETAILS
  }
}

function QuoteRequestFlowImpl ({
  dashboardUrl,
  initialStep,
  allowedCensusTypes,
  allowedSbcTypes,
  allowedOtherFileTypes,
}: QuoteRequestFlowProps) {
  const { search } = useLocation()

  return (
    <Routes>
      <Route path='broker/quote_request/:id' element={<QuoteRequestLayout dashboardUrl={dashboardUrl} />}>
        <Route index element={<Navigate to={getStepRoute(initialStep)} replace />} />
        <Route path='client-details' element={<ClientDetailsPage dashboardUrl={dashboardUrl} />} />
        <Route path='upload'>
          <Route path='census' element={<CensusUploadPage accept={allowedCensusTypes} />} />
          <Route path='sbcs' element={<SbcsUploadPage accept={allowedSbcTypes} />} />
          <Route path='other' element={<OtherFilesUploadPage accept={allowedOtherFileTypes} />} />
        </Route>
        <Route path='summary'>
          <Route index element={<SummaryPage />} />
          <Route path='edit' element={<SummaryEditPage />} />
        </Route>
        <Route path='*' element={<Navigate to={{ pathname: '../client-details', search }} replace />} />
      </Route>
    </Routes>
  )
}

export function QuoteRequestFlow ({
  dashboardUrl,
  initialStep,
  allowedCensusTypes,
  allowedSbcTypes,
  allowedOtherFileTypes,
  router: Router = BrowserRouter,
  routerProps,
}: QuoteRequestFlowPropsUnion) {
  return (
    <Router {...routerProps}>
      <QuoteRequestFlowImpl
        dashboardUrl={dashboardUrl}
        initialStep={initialStep}
        allowedCensusTypes={allowedCensusTypes}
        allowedSbcTypes={allowedSbcTypes}
        allowedOtherFileTypes={allowedOtherFileTypes}
      />
    </Router>
  )
}
